import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, Checkbox, Divider, Stack } from '@mui/material';

const COOKIE_PREFERENCES_KEY = 'cookiePreferences';

const defaultPreferences = {
  essential: true, // Always required
  analytics: true,
  marketing: true,
};

const CookieConsent = () => {
  const [open, setOpen] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [preferences, setPreferences] = useState(defaultPreferences);

  // Check localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_PREFERENCES_KEY);
    if (stored) {
      setPreferences(JSON.parse(stored));
      setOpen(false);
    }
  }, []);

  const handleAcceptAll = () => {
    const newPrefs = { essential: true, analytics: true, marketing: true };
    setPreferences(newPrefs);
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(newPrefs));
    setOpen(false);
  };

  const handleRejectAll = () => {
    const newPrefs = { essential: true, analytics: false, marketing: false };
    setPreferences(newPrefs);
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(newPrefs));
    setOpen(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));
    setOpen(false);
  };

  const togglePreference = (key) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!open) return null;

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        p: 2,
        zIndex: 1300,
        borderTop: '1px solid',
        borderColor: 'grey.300',
        bgcolor: 'white',
      }}
      elevation={3}
    >
      <Box
        maxWidth="xl"
        mx="auto"
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box flex={1}>
            <Typography
              variant="h6"
              component="h2"
              fontWeight="bold"
              mb={1}
            >
              Cookie Settings
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
            >
              We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic.
              By clicking "Accept All", you consent to our use of cookies.
            </Typography>
          </Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
          >
            <Button
              variant="contained"
              onClick={handleAcceptAll}
            >
              Accept All
            </Button>
            <Button
              variant="outlined"
              onClick={handleRejectAll}
            >
              Reject All
            </Button>
            <Button
              variant="outlined"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? 'Hide Preferences' : 'Customize'}
            </Button>
          </Stack>
        </Stack>

        {expanded && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                  >
                    Essential Cookies
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    These cookies are necessary for the website to function and cannot be switched off. They are usually
                    only set in response to actions made by you which amount to a request for services. These include
                    remembering your preferences, such as text size and language.
                  </Typography>
                </Box>
                <Checkbox
                  checked
                  disabled
                  sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                  >
                    Analytics Cookies
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    These cookies allow us to count visits and traffic sources so we can measure and improve the
                    performance of our site. They help us to know which pages are the most and least popular and useful.
                    We also record session data to identify and fix issues you might encounter. No personally
                    identifiable information is collected.
                  </Typography>
                </Box>
                <Checkbox
                  checked={preferences.analytics}
                  onChange={() => togglePreference('analytics')}
                  sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                  >
                    Marketing Cookies
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    These cookies may be set through our site by our advertising partners. They are used to improve our
                    ability to show our site to people who will be the most interested in it.
                  </Typography>
                </Box>
                <Checkbox
                  checked={preferences.marketing}
                  onChange={() => togglePreference('marketing')}
                  sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                />
              </Box>
            </Stack>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleSavePreferences}
              >
                Save Preferences
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default CookieConsent;
